import { useState, useEffect, useCallback } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";

const LAYOUT_API = `${process.env.REACT_APP_BACKEND_URL}/api`;
import {
  LayoutDashboard,
  Plane,
  BookOpenCheck,
  FileText,
  Receipt,
  Ticket,
  Users,
  Calculator,
  ClipboardList,
  ScrollText,
  LogOut,
  User,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  X,
  CreditCard,
  Clock,
  Archive,
  BedDouble,
  Inbox,
  Moon,
  MapPin,
  Bot,
  UserCircle,
} from "lucide-react";

const LOGO_URL = "/images/logo.png";

const FLIGHT_PATHS = ["/requests", "/quotes", "/bookings"];
const VISA_PATHS = ["/visa-requests", "/visa-quotes"];
const HOTEL_PATHS = ["/hotel-requests", "/hotel-bookings"];
const UMRAH_PATHS = ["/umrah-packages", "/umrah-quotations", "/umrah-bookings"];

const inGroup = (pathname, paths) =>
  paths.some(p => pathname === p || pathname.startsWith(p + "/"));

const ls = {
  get: (key, fallback) => {
    try { const v = localStorage.getItem(key); return v === null ? fallback : v; }
    catch { return fallback; }
  },
  set: (key, value) => { try { localStorage.setItem(key, String(value)); } catch {} },
};

export const DashboardLayout = ({ children }) => {
  const { agent, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [collapsed, setCollapsed] = useState(() =>
    ls.get("tbf-sidebar-collapsed", "false") === "true"
  );

  const [flightsOpen, setFlightsOpen] = useState(() =>
    inGroup(location.pathname, FLIGHT_PATHS) ||
    ls.get("tbf-flights-open", "true") !== "false"
  );

  const [visasOpen, setVisasOpen] = useState(() =>
    inGroup(location.pathname, VISA_PATHS) ||
    ls.get("tbf-visas-open", "false") === "true"
  );

  const [hotelsOpen, setHotelsOpen] = useState(() =>
    inGroup(location.pathname, HOTEL_PATHS) ||
    ls.get("tbf-hotels-open", "false") === "true"
  );

  const [umrahOpen, setUmrahOpen] = useState(() =>
    inGroup(location.pathname, UMRAH_PATHS) ||
    ls.get("tbf-umrah-open", "false") === "true"
  );

  const [mobileGroup, setMobileGroup] = useState(null);
  const [fareAlertCount, setFareAlertCount] = useState(0);

  useEffect(() => { ls.set("tbf-sidebar-collapsed", collapsed); }, [collapsed]);
  useEffect(() => { ls.set("tbf-flights-open", flightsOpen); }, [flightsOpen]);
  useEffect(() => { ls.set("tbf-visas-open", visasOpen); }, [visasOpen]);
  useEffect(() => { ls.set("tbf-hotels-open", hotelsOpen); }, [hotelsOpen]);
  useEffect(() => { ls.set("tbf-umrah-open", umrahOpen); }, [umrahOpen]);

  useEffect(() => {
    if (inGroup(location.pathname, FLIGHT_PATHS)) setFlightsOpen(true);
    if (inGroup(location.pathname, VISA_PATHS)) setVisasOpen(true);
    if (inGroup(location.pathname, HOTEL_PATHS)) setHotelsOpen(true);
    if (inGroup(location.pathname, UMRAH_PATHS)) setUmrahOpen(true);
    setMobileGroup(null);
  }, [location.pathname]);

  const { getAuthHeaders } = useAuth();
  const fetchFareAlertCount = useCallback(async () => {
    try {
      const { data } = await axios.get(`${LAYOUT_API}/fare-alerts?unread_only=true`, {
        headers: getAuthHeaders(),
      });
      setFareAlertCount(Array.isArray(data) ? data.length : 0);
    } catch { /* non-critical */ }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchFareAlertCount();
    const interval = setInterval(fetchFareAlertCount, 5 * 60 * 1000); // poll every 5 min
    return () => clearInterval(interval);
  }, [fetchFareAlertCount]);

  const handleLogout = () => { logout(); navigate("/login"); };

  const isFlightsActive = inGroup(location.pathname, FLIGHT_PATHS);
  const isVisasActive = inGroup(location.pathname, VISA_PATHS);
  const isHotelsActive = inGroup(location.pathname, HOTEL_PATHS);
  const isUmrahActive = inGroup(location.pathname, UMRAH_PATHS);

  const topLinkClass = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150 ${
      collapsed ? "justify-center px-0" : ""
    } ${isActive
      ? "bg-indigo-600 text-white"
      : "text-slate-600 hover:bg-indigo-50 hover:text-indigo-700"
    }`;

  const groupHeaderClass = (isActive) =>
    `flex items-center w-full px-4 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150 ${
      collapsed ? "justify-center px-0" : "justify-between"
    } ${isActive
      ? "text-indigo-700 bg-indigo-50"
      : "text-slate-600 hover:bg-indigo-50 hover:text-indigo-700"
    }`;

  const subLinkClass = ({ isActive }) =>
    `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors duration-150 ${
      isActive
        ? "text-indigo-700 bg-indigo-100 font-semibold"
        : "text-slate-500 hover:text-indigo-700 hover:bg-indigo-50"
    }`;

  const mobileLinkClass = ({ isActive }) =>
    `flex flex-col items-center gap-0.5 px-2 py-1 rounded-lg min-w-0 flex-1 ${
      isActive ? "text-indigo-700" : "text-gray-400"
    }`;

  return (
    <div className="h-screen overflow-hidden bg-indigo-50/30 flex">

      {/* ── Desktop Sidebar ──────────────────────────────────── */}
      <aside
        data-testid="dashboard-sidebar"
        className={`hidden md:flex flex-col bg-white border-r border-indigo-100 shrink-0 transition-all duration-300 ease-in-out ${
          collapsed ? "w-[60px]" : "w-64"
        }`}
        style={{ minHeight: "100%" }}
      >
        {/* Logo + collapse toggle */}
        <div
          className={`flex items-center border-b border-indigo-100 h-[74px] ${
            collapsed ? "flex-col justify-center gap-1.5" : "px-4 justify-between"
          }`}
        >
          {collapsed ? (
            <>
              <NavLink to="/dashboard">
                <img
                  src="/images/favicon.png"
                  alt="The Better Fare"
                  className="w-8 h-8 object-contain"
                />
              </NavLink>
              <button
                onClick={() => setCollapsed(false)}
                title="Expand sidebar"
                className="flex items-center justify-center w-8 h-6 rounded-md text-slate-400 hover:text-indigo-700 hover:bg-indigo-50 transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </>
          ) : (
            <>
              <NavLink to="/dashboard" className="flex items-center gap-2 min-w-0">
                <img src={LOGO_URL} alt="The Better Fare" className="h-10 w-auto object-contain shrink-0" />
                <span className="font-display text-base font-bold text-indigo-700 leading-tight truncate">Nexa</span>
              </NavLink>
              <button
                onClick={() => setCollapsed(true)}
                title="Collapse sidebar"
                className="flex items-center justify-center w-8 h-8 rounded-md text-slate-300 hover:text-slate-600 hover:bg-slate-100 transition-colors shrink-0"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
            </>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto">

          {/* Dashboard */}
          <NavLink
            to="/dashboard"
            title="Dashboard"
            data-testid="nav-dashboard"
            className={topLinkClass}
          >
            <LayoutDashboard className="w-5 h-5 shrink-0" />
            {!collapsed && <span>Dashboard</span>}
          </NavLink>

          {/* Flights group */}
          <div>
            <button
              title={collapsed ? "Flights" : undefined}
              onClick={() =>
                collapsed
                  ? (setCollapsed(false), setFlightsOpen(true))
                  : setFlightsOpen(o => !o)
              }
              className={groupHeaderClass(isFlightsActive)}
            >
              <span className="flex items-center gap-3">
                <Plane className="w-5 h-5 shrink-0" />
                {!collapsed && <span>Flights</span>}
              </span>
              {!collapsed && (
                <ChevronDown
                  className={`w-4 h-4 shrink-0 transition-transform duration-200 ${
                    flightsOpen ? "" : "-rotate-90"
                  }`}
                />
              )}
            </button>

            {!collapsed && flightsOpen && (
              <div className="ml-3 mt-1 pl-3 border-l border-indigo-100 space-y-0.5 pb-1">
                <NavLink
                  to="/requests"
                  className={subLinkClass}
                  data-testid="nav-flights-requests"
                >
                  <FileText className="w-4 h-4 shrink-0" />
                  <span className="flex-1">Requests</span>
                  {fareAlertCount > 0 && (
                    <span className="ml-1 inline-flex items-center justify-center rounded-full bg-indigo-600 text-white text-[9px] font-bold w-4 h-4 shrink-0">
                      {fareAlertCount > 9 ? "9+" : fareAlertCount}
                    </span>
                  )}
                </NavLink>
                <NavLink
                  to="/quotes"
                  className={subLinkClass}
                  data-testid="nav-flights-quotes"
                >
                  <Receipt className="w-4 h-4 shrink-0" />
                  <span>Quotes</span>
                </NavLink>
                <NavLink
                  to="/bookings"
                  className={subLinkClass}
                  data-testid="nav-flights-bookings"
                >
                  <Ticket className="w-4 h-4 shrink-0" />
                  <span>Bookings</span>
                </NavLink>
              </div>
            )}
          </div>

          {/* Visas group */}
          <div>
            <button
              title={collapsed ? "Visas" : undefined}
              onClick={() =>
                collapsed
                  ? (setCollapsed(false), setVisasOpen(true))
                  : setVisasOpen(o => !o)
              }
              className={groupHeaderClass(isVisasActive)}
            >
              <span className="flex items-center gap-3">
                <BookOpenCheck className="w-5 h-5 shrink-0" />
                {!collapsed && <span>Visas</span>}
              </span>
              {!collapsed && (
                <ChevronDown
                  className={`w-4 h-4 shrink-0 transition-transform duration-200 ${
                    visasOpen ? "" : "-rotate-90"
                  }`}
                />
              )}
            </button>

            {!collapsed && visasOpen && (
              <div className="ml-3 mt-1 pl-3 border-l border-indigo-100 space-y-0.5 pb-1">
                <NavLink
                  to="/visa-requests"
                  end
                  className={subLinkClass}
                  data-testid="nav-visas-requests"
                >
                  <FileText className="w-4 h-4 shrink-0" />
                  <span>Requests</span>
                </NavLink>
                <NavLink
                  to="/visa-quotes"
                  className={subLinkClass}
                  data-testid="nav-visas-quotes"
                >
                  <Receipt className="w-4 h-4 shrink-0" />
                  <span>Quotes</span>
                </NavLink>
              </div>
            )}
          </div>

          {/* Hotels group */}
          <div>
            <button
              title={collapsed ? "Hotels" : undefined}
              onClick={() =>
                collapsed
                  ? (setCollapsed(false), setHotelsOpen(true))
                  : setHotelsOpen(o => !o)
              }
              className={groupHeaderClass(isHotelsActive)}
              data-testid="nav-hotels"
            >
              <BedDouble className="w-5 h-5 shrink-0" />
              {!collapsed && <span>Hotels</span>}
              {!collapsed && (
                <ChevronDown
                  className={`w-4 h-4 ml-auto transition-transform ${
                    hotelsOpen ? "" : "-rotate-90"
                  }`}
                />
              )}
            </button>
            {!collapsed && hotelsOpen && (
              <div className="ml-3 mt-1 pl-3 border-l border-indigo-100 space-y-0.5 pb-1">
                <NavLink
                  to="/hotel-requests"
                  className={subLinkClass}
                  data-testid="nav-hotels-requests"
                >
                  <Inbox className="w-4 h-4 shrink-0" />
                  <span>Requests</span>
                </NavLink>
                <NavLink
                  to="/hotel-bookings"
                  className={subLinkClass}
                  data-testid="nav-hotels-bookings"
                >
                  <BedDouble className="w-4 h-4 shrink-0" />
                  <span>Bookings</span>
                </NavLink>
              </div>
            )}
          </div>

          {/* Umrah group */}
          <div>
            <button
              title={collapsed ? "Umrah" : undefined}
              onClick={() =>
                collapsed
                  ? (setCollapsed(false), setUmrahOpen(true))
                  : setUmrahOpen(o => !o)
              }
              className={groupHeaderClass(isUmrahActive)}
              data-testid="nav-umrah"
            >
              <span className="flex items-center gap-3">
                <Moon className="w-5 h-5 shrink-0" />
                {!collapsed && <span>Umrah</span>}
              </span>
              {!collapsed && (
                <ChevronDown
                  className={`w-4 h-4 shrink-0 transition-transform duration-200 ${
                    umrahOpen ? "" : "-rotate-90"
                  }`}
                />
              )}
            </button>
            {!collapsed && umrahOpen && (
              <div className="ml-3 mt-1 pl-3 border-l border-amber-100 space-y-0.5 pb-1">
                <NavLink
                  to="/umrah-packages"
                  className={subLinkClass}
                  data-testid="nav-umrah-packages"
                >
                  <Moon className="w-4 h-4 shrink-0" />
                  <span>Packages</span>
                </NavLink>
                <NavLink
                  to="/umrah-quotations"
                  className={subLinkClass}
                  data-testid="nav-umrah-quotations"
                >
                  <Moon className="w-4 h-4 shrink-0" />
                  <span>Quotations</span>
                </NavLink>
                <NavLink
                  to="/umrah-bookings"
                  className={subLinkClass}
                  data-testid="nav-umrah-bookings"
                >
                  <Moon className="w-4 h-4 shrink-0" />
                  <span>Bookings</span>
                </NavLink>
              </div>
            )}
          </div>

          {/* Contacts */}
          <NavLink
            to="/contacts"
            title="Contacts"
            data-testid="nav-contacts"
            className={topLinkClass}
          >
            <Users className="w-5 h-5 shrink-0" />
            {!collapsed && <span>Contacts</span>}
          </NavLink>

          {/* Provisional Itineraries */}
          <NavLink
            to="/provisional-itineraries"
            title="Provisional Itineraries"
            data-testid="nav-provisional-itineraries"
            className={topLinkClass}
          >
            <MapPin className="w-5 h-5 shrink-0" />
            {!collapsed && <span>Itineraries</span>}
          </NavLink>

          {/* Invoices */}
          <NavLink
            to="/invoices"
            title="Invoices"
            data-testid="nav-invoices"
            className={topLinkClass}
          >
            <ClipboardList className="w-5 h-5 shrink-0" />
            {!collapsed && <span>Invoices</span>}
          </NavLink>

          {/* Receipts */}
          <NavLink
            to="/receipts"
            title="Receipts"
            data-testid="nav-receipts"
            className={topLinkClass}
          >
            <ScrollText className="w-5 h-5 shrink-0" />
            {!collapsed && <span>Receipts</span>}
          </NavLink>

          {/* Bookkeeping */}
          <NavLink
            to="/accounting"
            title="Bookkeeping"
            data-testid="nav-bookkeeping"
            className={topLinkClass}
          >
            <Calculator className="w-5 h-5 shrink-0" />
            {!collapsed && <span>Bookkeeping</span>}
          </NavLink>

          {/* Payment Settings */}
          <NavLink
            to="/settings/payment"
            title="Payment Settings"
            data-testid="nav-payment-settings"
            className={topLinkClass}
          >
            <CreditCard className="w-5 h-5 shrink-0" />
            {!collapsed && <span>Payment Settings</span>}
          </NavLink>

          {/* Working Hours */}
          <NavLink
            to="/working-hours"
            title="Working Hours"
            data-testid="nav-working-hours"
            className={topLinkClass}
          >
            <Clock className="w-5 h-5 shrink-0" />
            {!collapsed && <span>Working Hours</span>}
          </NavLink>

          {/* Document Archive */}
          <NavLink
            to="/document-archive"
            title="Document Archive"
            data-testid="nav-document-archive"
            className={topLinkClass}
          >
            <Archive className="w-5 h-5 shrink-0" />
            {!collapsed && <span>Document Archive</span>}
          </NavLink>

          {/* AI Agent */}
          <NavLink
            to="/agent-activity"
            title="AI Agent"
            data-testid="nav-agent-activity"
            className={topLinkClass}
          >
            <Bot className="w-5 h-5 shrink-0" />
            {!collapsed && <span>AI Agent</span>}
          </NavLink>

          {/* Employees — admin only */}
          {agent?.role === "admin" && (
            <NavLink
              to="/employees"
              title="Employees"
              data-testid="nav-employees"
              className={topLinkClass}
            >
              <UserCircle className="w-5 h-5 shrink-0" />
              {!collapsed && <span>Employees</span>}
            </NavLink>
          )}
        </nav>

        {/* User profile + actions */}
        <div className="border-t border-indigo-100 px-2 py-3 space-y-1">
          {collapsed ? (
            <div
              className="flex justify-center py-1"
              title={`${agent?.name}\n${agent?.email}`}
            >
              <div className="w-8 h-8 bg-indigo-200 rounded-full flex items-center justify-center">
                <User className="w-4 h-4 text-indigo-700" />
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2.5 px-3 py-2 bg-indigo-50 rounded-lg">
              <div className="w-8 h-8 bg-indigo-200 rounded-full flex items-center justify-center shrink-0">
                <User className="w-4 h-4 text-indigo-700" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-indigo-900 truncate">{agent?.name}</p>
                <p className="text-xs text-indigo-600 truncate">{agent?.email}</p>
              </div>
            </div>
          )}

          <button
            title={collapsed ? "Sign Out" : undefined}
            onClick={handleLogout}
            data-testid="logout-btn"
            className={`flex items-center gap-3 w-full px-3 py-2 rounded-lg text-sm text-slate-500 hover:text-red-600 hover:bg-red-50 transition-colors ${
              collapsed ? "justify-center" : ""
            }`}
          >
            <LogOut className="w-4 h-4 shrink-0" />
            {!collapsed && <span>Sign Out</span>}
          </button>
        </div>
      </aside>

      {/* ── Main Content ─────────────────────────────────────── */}
      <main className="flex-1 overflow-y-auto w-full mobile-nav-pb">
        {children}
      </main>

      {/* ── Mobile: Sub-group flyout panel ───────────────────── */}
      {mobileGroup && (
        <div
          className="md:hidden fixed left-0 right-0 bg-white z-50"
          style={{ bottom: "calc(56px + env(safe-area-inset-bottom, 0px))", boxShadow: "0 -4px 20px rgba(0,0,0,0.12)" }}
        >
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100">
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-widest">
              {mobileGroup === "flights" ? "Flights" : mobileGroup === "hotels" ? "Hotels" : mobileGroup === "umrah" ? "Umrah" : mobileGroup === "more" ? "More" : "Visas"}
            </span>
            <button
              onClick={() => setMobileGroup(null)}
              className="p-1 text-slate-400 hover:text-slate-600 rounded-md"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          {mobileGroup === "more" ? (
            <div className="grid grid-cols-3 gap-0 divide-x divide-slate-100">
              {[
                { to: "/umrah-packages", icon: Moon, label: "Umrah" },
                { to: "/provisional-itineraries", icon: MapPin, label: "Itineraries" },
                { to: "/working-hours", icon: Clock, label: "Hours" },
                { to: "/document-archive", icon: Archive, label: "Archive" },
                { to: "/agent-activity", icon: Bot, label: "AI Agent" },
              ].map((item) => {
                const itemActive =
                  location.pathname === item.to ||
                  location.pathname.startsWith(item.to + "/");
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={() => setMobileGroup(null)}
                    className={`flex flex-col items-center gap-1.5 py-3 px-2 ${
                      itemActive ? "text-indigo-700 bg-indigo-50" : "text-slate-500"
                    }`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="text-xs font-medium">{item.label}</span>
                  </NavLink>
                );
              })}
              <button
                onClick={() => { setMobileGroup(null); handleLogout(); }}
                className="flex flex-col items-center gap-1.5 py-3 px-2 text-red-500"
              >
                <LogOut className="w-5 h-5" />
                <span className="text-xs font-medium">Sign Out</span>
              </button>
            </div>
          ) : (
          <div className="flex">
            {(mobileGroup === "flights"
              ? [
                  { to: "/requests", icon: FileText, label: "Requests" },
                  { to: "/quotes", icon: Receipt, label: "Quotes" },
                  { to: "/bookings", icon: Ticket, label: "Bookings" },
                ]
              : mobileGroup === "hotels"
              ? [
                  { to: "/hotel-requests", icon: Inbox, label: "Requests" },
                  { to: "/hotel-bookings", icon: BedDouble, label: "Bookings" },
                ]
              : mobileGroup === "umrah"
              ? [
                  { to: "/umrah-packages", icon: Moon, label: "Packages" },
                  { to: "/umrah-quotations", icon: Moon, label: "Quotations" },
                  { to: "/umrah-bookings", icon: Moon, label: "Bookings" },
                ]
              : [
                  { to: "/visa-requests", icon: FileText, label: "Requests" },
                  { to: "/visa-quotes",   icon: Receipt, label: "Quotes" },
                ]
            ).map((item, idx, arr) => {
              const isLast = idx === arr.length - 1;
              const itemActive =
                location.pathname === item.to ||
                location.pathname.startsWith(item.to + "/");
              return (
                <NavLink
                  key={item.to + item.label}
                  to={item.to}
                  onClick={() => setMobileGroup(null)}
                  className={`flex-1 flex flex-col items-center gap-1.5 py-3 px-2 ${
                    !isLast ? "border-r border-slate-100" : ""
                  } ${itemActive ? "text-indigo-700 bg-indigo-50" : "text-slate-500"}`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="text-xs font-medium">{item.label}</span>
                </NavLink>
              );
            })}
          </div>
          )}
        </div>
      )}

      {/* ── Mobile Bottom Navigation ─────────────────────────── */}
      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-indigo-100 z-50 flex items-center justify-around px-1 pt-1.5"
        style={{ paddingBottom: "calc(6px + env(safe-area-inset-bottom, 0px))", boxShadow: "0 -2px 8px rgba(0,0,0,0.08)" }}
      >
        {/* Dashboard */}
        <NavLink to="/dashboard" className={mobileLinkClass}>
          {({ isActive }) => (
            <>
              <div className={`p-1 rounded-lg ${isActive ? "bg-indigo-100" : ""}`}>
                <LayoutDashboard className="w-5 h-5" />
              </div>
              <span style={{ fontSize: "9px", fontWeight: isActive ? 700 : 500, lineHeight: 1 }}>
                Dashboard
              </span>
            </>
          )}
        </NavLink>

        {/* Flights (expands sub-panel) */}
        <button
          onClick={() => setMobileGroup(g => g === "flights" ? null : "flights")}
          className={`flex flex-col items-center gap-0.5 px-2 py-1 rounded-lg flex-1 ${
            isFlightsActive || mobileGroup === "flights" ? "text-indigo-700" : "text-gray-400"
          }`}
        >
          <div
            className={`p-1 rounded-lg ${
              isFlightsActive || mobileGroup === "flights" ? "bg-indigo-100" : ""
            }`}
          >
            <Plane className="w-5 h-5" />
          </div>
          <span
            style={{
              fontSize: "9px",
              fontWeight: isFlightsActive || mobileGroup === "flights" ? 700 : 500,
              lineHeight: 1,
            }}
          >
            Flights
          </span>
        </button>

        {/* Visas (expands sub-panel) */}
        <button
          onClick={() => setMobileGroup(g => g === "visas" ? null : "visas")}
          className={`flex flex-col items-center gap-0.5 px-2 py-1 rounded-lg flex-1 ${
            isVisasActive || mobileGroup === "visas" ? "text-indigo-700" : "text-gray-400"
          }`}
        >
          <div
            className={`p-1 rounded-lg ${
              isVisasActive || mobileGroup === "visas" ? "bg-indigo-100" : ""
            }`}
          >
            <BookOpenCheck className="w-5 h-5" />
          </div>
          <span
            style={{
              fontSize: "9px",
              fontWeight: isVisasActive || mobileGroup === "visas" ? 700 : 500,
              lineHeight: 1,
            }}
          >
            Visas
          </span>
        </button>

        {/* Hotels (expands sub-panel) */}
        <button
          onClick={() => setMobileGroup(g => g === "hotels" ? null : "hotels")}
          className={`flex flex-col items-center gap-0.5 px-2 py-1 rounded-lg flex-1 ${
            isHotelsActive || mobileGroup === "hotels" ? "text-indigo-700" : "text-gray-400"
          }`}
        >
          <div className={`p-1 rounded-lg ${isHotelsActive || mobileGroup === "hotels" ? "bg-indigo-100" : ""}`}>
            <BedDouble className="w-5 h-5" />
          </div>
          <span style={{ fontSize: "9px", fontWeight: isHotelsActive || mobileGroup === "hotels" ? 700 : 500, lineHeight: 1 }}>
            Hotels
          </span>
        </button>

        {/* Umrah (expands sub-panel) */}
        <button
          onClick={() => setMobileGroup(g => g === "umrah" ? null : "umrah")}
          className={`flex flex-col items-center gap-0.5 px-2 py-1 rounded-lg flex-1 ${
            isUmrahActive || mobileGroup === "umrah" ? "text-indigo-700" : "text-gray-400"
          }`}
        >
          <div className={`p-1 rounded-lg ${isUmrahActive || mobileGroup === "umrah" ? "bg-indigo-100" : ""}`}>
            <Moon className="w-5 h-5" />
          </div>
          <span style={{ fontSize: "9px", fontWeight: isUmrahActive || mobileGroup === "umrah" ? 700 : 500, lineHeight: 1 }}>
            Umrah
          </span>
        </button>

        {/* Contacts */}
        <NavLink to="/contacts" className={mobileLinkClass}>
          {({ isActive }) => (
            <>
              <div className={`p-1 rounded-lg ${isActive ? "bg-indigo-100" : ""}`}>
                <Users className="w-5 h-5" />
              </div>
              <span style={{ fontSize: "9px", fontWeight: isActive ? 700 : 500, lineHeight: 1 }}>
                Contacts
              </span>
            </>
          )}
        </NavLink>

        {/* Invoices */}
        <NavLink to="/invoices" className={mobileLinkClass}>
          {({ isActive }) => (
            <>
              <div className={`p-1 rounded-lg ${isActive ? "bg-indigo-100" : ""}`}>
                <ClipboardList className="w-5 h-5" />
              </div>
              <span style={{ fontSize: "9px", fontWeight: isActive ? 700 : 500, lineHeight: 1 }}>
                Invoices
              </span>
            </>
          )}
        </NavLink>

        {/* Receipts */}
        <NavLink to="/receipts" className={mobileLinkClass}>
          {({ isActive }) => (
            <>
              <div className={`p-1 rounded-lg ${isActive ? "bg-indigo-100" : ""}`}>
                <ScrollText className="w-5 h-5" />
              </div>
              <span style={{ fontSize: "9px", fontWeight: isActive ? 700 : 500, lineHeight: 1 }}>
                Receipts
              </span>
            </>
          )}
        </NavLink>

        {/* Bookkeeping */}
        <NavLink to="/accounting" className={mobileLinkClass}>
          {({ isActive }) => (
            <>
              <div className={`p-1 rounded-lg ${isActive ? "bg-indigo-100" : ""}`}>
                <Calculator className="w-5 h-5" />
              </div>
              <span style={{ fontSize: "9px", fontWeight: isActive ? 700 : 500, lineHeight: 1 }}>
                Bookkeeping
              </span>
            </>
          )}
        </NavLink>

        {/* More (Working Hours, Document Archive, AI Agent, Provisional Itineraries, Sign Out) */}
        <button
          onClick={() => setMobileGroup(g => g === "more" ? null : "more")}
          className={`flex flex-col items-center gap-0.5 px-2 py-1 rounded-lg flex-1 ${
            mobileGroup === "more" ? "text-indigo-700" : "text-gray-400"
          }`}
        >
          <div className={`p-1 rounded-lg ${mobileGroup === "more" ? "bg-indigo-100" : ""}`}>
            <ChevronDown className="w-5 h-5" />
          </div>
          <span style={{ fontSize: "9px", fontWeight: mobileGroup === "more" ? 700 : 500, lineHeight: 1 }}>More</span>
        </button>
      </nav>
    </div>
  );
};
